import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { message } from 'antd';
import { authService } from '../services/authService';

// Initial state
const initialState = {
  user: null,
  loading: true,
  error: null,
};

// Action types
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  SET_ERROR: 'SET_ERROR',
  LOGOUT: 'LOGOUT',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        loading: false,
        error: null,
      };
    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        loading: false,
        error: null,
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check if user is authenticated on app load
  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates if component unmounts

    const checkAuthStatus = async () => {
      try {
        if (!isMounted) return; // Prevent duplicate calls

        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

        // Check for OAuth callback success parameter
        const urlParams = new URLSearchParams(window.location.search);
        const authStatus = urlParams.get('auth');
        const error = urlParams.get('error');

        if (authStatus === 'success') {
          // Clear the URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          message.success('Login successful! Welcome to COGS Management.');
        } else if (error) {
          // Clear the URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);

          // Provide specific error messages
          let errorMessage = 'Login failed';
          if (error === 'unauthorized_domain') {
            errorMessage = 'Only @xendit.co email addresses are allowed to access this system.';
          } else {
            errorMessage = `Login failed: ${error.replace(/_/g, ' ')}`;
          }

          message.error(errorMessage, 5); // Show for 5 seconds
        }

        const user = await authService.getCurrentUser();

        if (isMounted) {
          dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
        }
      } catch (error) {
        if (isMounted) {
          dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
        }
      }
    };

    checkAuthStatus();

    // Cleanup function to prevent memory leaks and duplicate calls
    return () => {
      isMounted = false;
    };
  }, []);

  const checkAuthStatus = async () => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      const user = await authService.getCurrentUser();
      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
    }
  };

  const login = async () => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      // Redirect to Google OAuth
      authService.initiateGoogleLogin();
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      message.error('Login failed. Please try again.');
    }
  };

  const logout = async () => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      await authService.logout();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      message.success('Logged out successfully');
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      message.error('Logout failed. Please try again.');
    }
  };

  const updateUser = (userData) => {
    dispatch({ type: AUTH_ACTIONS.SET_USER, payload: userData });
  };

  const value = {
    user: state.user,
    loading: state.loading,
    error: state.error,
    login,
    logout,
    updateUser,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
